import * as maplibregl from 'maplibre-gl';
import mapWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Protocol } from 'pmtiles';
import style from './style.json';
import { Route } from './route';
import { searchDAWA, type SearchHit } from './search';
import { saveRoute, loadRoute } from './share';
import { toGPX } from './gpx';

// All routing currently uses the pedestrian profile. The activity dropdown
// (Run/Walk/Bike) was removed because Run+Walk both mapped to pedestrian
// already and bike planning is out of scope for v1. Bring it back as a
// proper toggle if/when a bike-route use case appears.
const FIXED_ACTIVITY: 'run' = 'run';
const FIXED_PROFILE: 'pedestrian' = 'pedestrian';

// MapLibre 6 ships a separate worker; let Vite bundle its dependencies.
maplibregl.setWorkerUrl(mapWorkerUrl);

const protocol = new Protocol();
maplibregl.addProtocol('pmtiles', protocol.tile);

const map = new maplibregl.Map({
  container: 'map',
  style: style as maplibregl.StyleSpecification,
  center: [10.5, 56.0],
  zoom: 6.5,
  // Bounds cover all of Denmark including Bornholm (out to ~15.2 E).
  maxBounds: [[7.5, 54.3], [15.5, 58.0]],
  // Required so window.print() captures the WebGL canvas in Chrome.
  // Without this, the printed map area comes out blank.
  canvasContextAttributes: { preserveDrawingBuffer: true },
});

map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

map.addControl(
  new maplibregl.GeolocateControl({
    positionOptions: { enableHighAccuracy: true },
    trackUserLocation: true,
    showUserLocation: true,
  }),
  'top-right',
);

map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');

// Mobile bottom-sheet collapse toggle. Tapping the drag-handle pill
// at the top of the panel toggles a body class; CSS does the height
// transition. We resize the map after the transition finishes so
// MapLibre re-renders to fit the new container size.
const panelToggle = document.getElementById('panel-toggle');
if (panelToggle) {
  panelToggle.addEventListener('click', () => {
    const collapsed = document.body.classList.toggle('panel-collapsed');
    panelToggle.setAttribute('aria-expanded', String(!collapsed));
    panelToggle.setAttribute('aria-label', collapsed ? 'Vis panel' : 'Skjul panel');
    // Wait for the CSS transition (0.25s) before triggering the map resize.
    setTimeout(() => map.resize(), 280);
  });
}

const route = new Route();
const markers: maplibregl.Marker[] = [];
const ROUTE_SOURCE_ID = 'afveje-route';
const ROUTE_LAYER_ID = 'afveje-route-line';

const snapEl = document.getElementById('snap') as HTMLInputElement;
const busyEl = document.getElementById('busy') as HTMLSpanElement;

async function withBusy<T>(fn: () => Promise<T>): Promise<T> {
  busyEl.hidden = false;
  try { return await fn(); }
  finally { busyEl.hidden = true; }
}

map.on('load', () => {
  map.addSource(ROUTE_SOURCE_ID, {
    type: 'geojson',
    data: emptyLine(),
  });
  map.addLayer({
    id: ROUTE_LAYER_ID,
    type: 'line',
    source: ROUTE_SOURCE_ID,
    paint: {
      'line-color': '#1e3a8a',
      'line-width': 5,
      'line-opacity': 0.85,
    },
    layout: { 'line-cap': 'round', 'line-join': 'round' },
  });
});

map.on('click', async (e) => {
  try {
    await withBusy(() =>
      route.addWaypointAsync(
        { lng: e.lngLat.lng, lat: e.lngLat.lat },
        snapEl.checked,
        FIXED_PROFILE,
      ),
    );
    clearRouteError();
  } catch (err) {
    // Route model is atomic - a failed fetch leaves the route untouched.
    // Just surface the error to the user instead of letting it propagate
    // as an unhandled promise rejection.
    showRouteError('Kunne ikke beregne ruten', err);
  }
  redraw();
});

document.getElementById('undo')!.addEventListener('click', () => {
  route.undo();
  redraw();
});

document.getElementById('clear')!.addEventListener('click', () => {
  route.clear();
  searchMarker?.remove();
  searchMarker = undefined;
  searchEl.value = '';
  redraw();
});

snapEl.addEventListener('change', async () => {
  // Snapshot the toggle state in case the user changes it mid-recompute.
  const want = snapEl.checked;
  try {
    await withBusy(() => route.recomputeAll(want, FIXED_PROFILE));
    clearRouteError();
  } catch (err) {
    // recomputeAll is transactional - on failure the previous good
    // route is still in place. Roll the toggle back so the UI matches
    // what the underlying segments actually represent.
    snapEl.checked = !want;
    showRouteError('Kunne ikke genberegne ruten', err);
  }
  redraw();
});

function showRouteError(prefix: string, err: unknown): void {
  const msg = err instanceof Error ? err.message : String(err);
  shareStatusEl.textContent = `${prefix}: ${msg}`;
}
function clearRouteError(): void {
  // Don't blow away a fresh "Link kopieret: ..." status - only clear if
  // what's currently shown looks like one of our error messages.
  if (shareStatusEl.textContent?.startsWith('Kunne ikke ')) {
    shareStatusEl.textContent = '';
  }
}

function redraw(): void {
  // Markers: add new ones, drop excess, ensure order matches waypoints.
  while (markers.length < route.waypoints.length) {
    const wp = route.waypoints[markers.length]!;
    markers.push(new maplibregl.Marker().setLngLat([wp.lng, wp.lat]).addTo(map));
  }
  while (markers.length > route.waypoints.length) {
    markers.pop()?.remove();
  }
  // Line.
  const src = map.getSource(ROUTE_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
  src?.setData({
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: route.geometry() },
  });
  // KM display: 2 decimals, Danish locale (comma).
  const km = route.meters / 1000;
  document.getElementById('km')!.textContent = km.toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  refreshShareEnabled();
}

function emptyLine(): GeoJSON.Feature<GeoJSON.LineString> {
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: [] },
  };
}

const shareEl = document.getElementById('share') as HTMLButtonElement;
const shareStatusEl = document.getElementById('share-status') as HTMLSpanElement;
const nameEl = document.getElementById('route-name') as HTMLInputElement;

const exportEl = document.getElementById('export-gpx') as HTMLButtonElement;

exportEl.addEventListener('click', () => {
  const coords = route.geometry();
  if (coords.length < 2) return;
  const name = nameEl.value.trim() || 'afveje rute';
  const xml = toGPX(name, coords);
  const blob = new Blob([xml], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = sanitizeFilename(name) + '.gpx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
});

function sanitizeFilename(s: string): string {
  return s.replace(/[^a-zA-Z0-9æøåÆØÅ_-]+/g, '_').replace(/^_+|_+$/g, '') || 'rute';
}

const printEl = document.getElementById('print') as HTMLButtonElement;

printEl.addEventListener('click', () => {
  const km = (route.meters / 1000).toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  document.getElementById('print-name')!.textContent = nameEl.value.trim() || 'Rute';
  document.getElementById('print-km')!.textContent = km + ' km';
  // Force a fresh paint so the canvas has the latest frame baked in (with
  // preserveDrawingBuffer the buffer is kept across frames, but we still
  // want the most recent state captured before the print dialog opens).
  map.triggerRepaint();
  setTimeout(() => window.print(), 50);
});

function refreshShareEnabled(): void {
  const enabled = route.waypoints.length >= 2;
  shareEl.disabled = !enabled;
  exportEl.disabled = !enabled;
  printEl.disabled = !enabled;
}

shareEl.addEventListener('click', async () => {
  if (route.waypoints.length < 2) return;
  shareEl.disabled = true;
  shareStatusEl.textContent = 'Gemmer…';
  try {
    const id = await saveRoute(route, {
      activity: FIXED_ACTIVITY,
      snap: snapEl.checked,
      name: nameEl.value.trim(),
    });
    const url = `${location.origin}/r/${id}`;
    history.replaceState(null, '', `/r/${id}`);
    try {
      await navigator.clipboard.writeText(url);
      shareStatusEl.textContent = 'Link kopieret: ' + url;
    } catch {
      shareStatusEl.textContent = url;
    }
    setTimeout(() => { shareStatusEl.textContent = ''; }, 8000);
  } catch (err) {
    shareStatusEl.textContent = 'Fejl: ' + (err instanceof Error ? err.message : String(err));
  } finally {
    refreshShareEnabled();
  }
});

const searchEl = document.getElementById('search') as HTMLInputElement;
const resultsEl = document.getElementById('search-results') as HTMLUListElement;
let searchTimer: number | undefined;
let searchAbort: AbortController | undefined;
let searchMarker: maplibregl.Marker | undefined;

searchEl.addEventListener('input', () => {
  window.clearTimeout(searchTimer);
  searchAbort?.abort();
  searchTimer = window.setTimeout(async () => {
    searchAbort = new AbortController();
    const hits = await searchDAWA(searchEl.value, searchAbort.signal).catch(() => []);
    renderResults(hits);
  }, 200);
});

searchEl.addEventListener('blur', () => {
  // Hide on blur, but use a small delay so a click on a result still fires.
  setTimeout(() => { resultsEl.hidden = true; }, 150);
});

searchEl.addEventListener('focus', () => {
  if (resultsEl.children.length > 0) resultsEl.hidden = false;
});

function renderResults(hits: SearchHit[]): void {
  resultsEl.innerHTML = '';
  for (const h of hits) {
    const li = document.createElement('li');
    li.textContent = h.tekst;
    li.addEventListener('mousedown', (ev) => {
      // mousedown so it fires before blur hides the list.
      ev.preventDefault();
      map.flyTo({ center: [h.lon, h.lat], zoom: 16 });
      searchMarker?.remove();
      searchMarker = new maplibregl.Marker({ color: '#dc2626' })
        .setLngLat([h.lon, h.lat])
        .setPopup(new maplibregl.Popup({ offset: 24 }).setText(h.tekst))
        .addTo(map);
      searchEl.value = h.tekst;
      resultsEl.hidden = true;
    });
    resultsEl.appendChild(li);
  }
  resultsEl.hidden = hits.length === 0;
}

const loadMatch = location.pathname.match(/^\/r\/([A-Za-z0-9_-]{8})$/);
if (loadMatch) {
  const id = loadMatch[1]!;
  map.once('load', async () => {
    const data = await loadRoute(id);
    if (!data) {
      shareStatusEl.textContent = 'Rute ikke fundet';
      return;
    }

    // Set the side-panel UI from the saved metadata.
    snapEl.checked = data.snap;
    nameEl.value = data.name;
    if (data.name) document.title = `${data.name} - afveje.dk`;

    // Stash the waypoints + seed the prebaked geometry as a single
    // segment so the route is immediately visible, measurable, and
    // exportable - even if Valhalla is unavailable. We then try to
    // upgrade to per-pair segments below; if that fails the user is
    // left with the prebaked single segment (lossy on undo but
    // functional). This is the explicit fallback the reviewer asked for.
    const wps = data.waypoints.map(([lng, lat]) => ({ lng, lat }));
    if (data.geometry.length >= 2) {
      route.loadPrebaked(wps, [{ coords: data.geometry, meters: data.meters }]);
    } else {
      route.loadPrebaked(wps, []);
    }

    // Frame the saved geometry immediately so the user sees something
    // useful before Valhalla's per-leg fetches finish.
    if (data.geometry.length > 0) {
      const [firstLng, firstLat] = data.geometry[0]!;
      const bounds = data.geometry.reduce(
        (b, c) => b.extend(c as [number, number]),
        new maplibregl.LngLatBounds([firstLng, firstLat], [firstLng, firstLat]),
      );
      map.fitBounds(bounds, { padding: 60, duration: 0 });
    }
    redraw();

    // Upgrade to segment-per-pair structure so undo() drops one leg at
    // a time instead of the entire polyline. recomputeAll is
    // transactional: if any leg fails (Valhalla down, network drop),
    // it throws and leaves the prebaked single segment in place.
    if (wps.length >= 2) {
      try {
        await withBusy(() => route.recomputeAll(data.snap, FIXED_PROFILE));
        redraw();
      } catch (err) {
        // Soft failure - the prebaked segment is still rendered, the
        // route is still measurable and exportable. Surface the
        // degraded state but do not block the user.
        showRouteError('Kunne ikke optimere den gemte rute', err);
      }
    }
  });
}
